// ✅ VerificationPassword.jsx — подтверждение кода для восстановления пароля с временной зоной
import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./verificationPassword.css";
import verification_mark from "../../assets/verification_mark.png";
import { IoMdLock } from "react-icons/io";
import { CiCircleQuestion } from "react-icons/ci";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === 'undefined') return 'UTC';
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function VerificationPassword() {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(20);
  const [tick, setTick] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState("UTC");
  const inputsRef = useRef([]);
  const navigate = useNavigate();

  const email = localStorage.getItem("pendingEmail");

  useEffect(() => {
    const timeZone = getUserTimeZone();
    setUserTimeZone(timeZone);
    localStorage.setItem("user_timezone", timeZone);
    
    localStorage.setItem("password_recovery_verification_start", new Date().toISOString());
    localStorage.setItem("password_recovery_timezone", timeZone);
  }, []);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft((t) => t - 1);
        setTick(true);
        setTimeout(() => setTick(false), 300);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const handleChange = (value, index) => {
    if (/^[0-9]?$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      if (value && index < code.length - 1) {
        inputsRef.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
    if (e.key === "Enter" && code.join("").length === 6 && !loading) {
      handleConfirm();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData("text").trim();
    if (/^\d+$/.test(paste)) {
      const digits = paste.split("").slice(0, code.length);
      const newCode = [...code];
      digits.forEach((digit, i) => (newCode[i] = digit));
      setCode(newCode);
      
      const lastFilledIndex = digits.length - 1;
      if (lastFilledIndex < code.length - 1) {
        setTimeout(() => {
          inputsRef.current[lastFilledIndex + 1]?.focus();
        }, 10);
      }
    }
    e.preventDefault();
  };

  const handleConfirm = async () => {
    if (loading) return;
    if (code.join("").length < 6) {
      setError("Please enter the full 6-digit code");
      return;
    }
    
    setError("");
    setLoading(true);

    try {
      const headers = buildHeaders();
      
      const res = await fetch(`${BASE_URL}/auth/verify-code/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          email,
          code: code.join(""),
          purpose: "password_recovery",
          timeZone: userTimeZone,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const errorMessage = data?.detail || "Invalid code. Try again.";
        setError(errorMessage);
        
        localStorage.setItem("password_recovery_verification_failed", new Date().toISOString());
        
        if (window.gtag) {
          window.gtag("event", "password_recovery_verification_error", {
            error_type: "invalid_code",
          });
        }
        
        return;
      }

      localStorage.setItem("recoveryCode", code.join(""));
      
      localStorage.setItem("password_recovery_verification_success", new Date().toISOString());
      localStorage.setItem("recovery_code_verification_timezone", userTimeZone);

      if (data?.access && data?.refresh) {
        localStorage.setItem("accessToken", data.access);
        localStorage.setItem("refreshToken", data.refresh);
      }

      if (window.gtag) {
        window.gtag("event", "password_recovery_code_verified", {
          email_domain: email?.split('@')[1],
        });
      }

      navigate("/reset-password", {
        state: { 
          timeZone: userTimeZone,
          verificationTimestamp: new Date().toISOString(),
        },
      });
    } catch (err) {
      console.error("❌ VERIFY ERROR:", err);
      setError("Verification failed. Try again later.");
      
      if (window.gtag) {
        window.gtag("event", "password_recovery_verification_error", {
          error_type: "network",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (timeLeft > 0) return;
    
    try {
      const headers = buildHeaders();
      
      const res = await fetch(`${BASE_URL}/auth/resend-code/`, {
        method: "POST",
        headers,
        body: JSON.stringify({ 
          email, 
          purpose: "password_recovery",
          timeZone: userTimeZone,
        }),
      });
      
      if (!res.ok) throw new Error();
      
      setTimeLeft(20);
      setCode(["", "", "", "", "", ""]);
      setError("");
      
      localStorage.setItem("password_recovery_code_resend", new Date().toISOString());
      localStorage.setItem("password_recovery_resend_count", 
        (parseInt(localStorage.getItem("password_recovery_resend_count") || 0) + 1).toString()
      );
      
      if (window.gtag) {
        window.gtag("event", "password_recovery_code_resent", {
          email_domain: email?.split('@')[1],
        });
      }
      
      toast.success("Verification code resent!");
      
      setTimeout(() => {
        inputsRef.current[0]?.focus();
      }, 100);
      
    } catch {
      setError("Failed to resend code. Please try again.");
    }
  };

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === "Enter" && code.join("").length === 6 && !loading) {
        e.preventDefault();
        handleConfirm();
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [code, loading]);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputsRef.current[0]?.focus();
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="verify">
      <main className="verify-main">
        <div className="verify-card">
          <div className="verify-avatar">
            <img src={verification_mark} alt="icon" />
          </div>
          <h2 className="verify-title">Verification Code</h2>
          <p className="verify-subtitle">
            We have sent a verification code to <b>{email}</b>
          </p>

          <div className="verify-code-inputs">
            {code.map((digit, i) => (
              <input
                key={i}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(e.target.value, i)}
                onKeyDown={(e) => handleKeyDown(e, i)}
                onPaste={handlePaste}
                ref={(el) => (inputsRef.current[i] = el)}
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                disabled={loading}
              />
            ))}
          </div>

          {error && <p className="verify-error-text">{error}</p>}

          {timeLeft > 0 ? (
            <p className="verify-resend-wrapper">
              <span className="verify-resend-label">Send code again</span>
              <span className={`verify-timer ${tick ? "tick" : ""}`}>
                00:{timeLeft.toString().padStart(2, "0")}
              </span>
            </p>
          ) : (
            <button
              className="verify-resend-btn fade-in"
              onClick={handleResend}
              disabled={loading}
            >
              Resend code
            </button>
          )}

          <button
            className="verify-confirm-btn"
            onClick={handleConfirm}
            disabled={loading || code.join("").length < 6}
          >
            {loading ? "Processing..." : "Confirm"}
          </button>
         
        </div>
      </main>

      <footer className="verify-footer">
        <span>© 2025 Halper. All rights reserved.</span>
        <div>
          <a href="#">
            <IoMdLock /> Privacy
          </a>
          <a href="#">
            <CiCircleQuestion /> Get help
          </a>
        </div>
      </footer>
    </div>
  );
}